import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Badge,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { getOutliersFacturas } from "../../helpers/backend_helper";
import WidgetLoader from "./WidgetLoader";

const ModalOutliersProveedor = ({ isOpen, toggle, proveedor, fechaInicio, fechaFin, aseguradora }) => {
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [facturasOutliers, setFacturasOutliers] = useState({ agudas: [], cronicas: [] });
  const [tabActiva, setTabActiva] = useState("agudas");

  useEffect(() => {
    if (isOpen && proveedor) {
      cargarFacturasOutliers();
    }
  }, [isOpen, proveedor, fechaInicio, fechaFin, aseguradora]);

  const cargarFacturasOutliers = async () => {
    setCargando(true);
    setError(null);

    try {
      const response = await getOutliersFacturas(proveedor.codigo, {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        aseguradora: aseguradora
      });
      
      if (response.data?.data) {
        setFacturasOutliers(response.data.data);
      }
    } catch (err) {
      console.error("Error al cargar facturas outliers:", err);
      setError("Error al cargar los detalles de las facturas.");
    } finally {
      setCargando(false);
    }
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString("es-VE", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatearNumero = (numero, decimales = 2) => {
    return Number(numero).toLocaleString("es-VE", {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  };

  const renderTablaFacturas = (facturas, tipo) => {
    if (!facturas || facturas.length === 0) {
      return (
        <Alert color="info" className="text-center">
          <i className="ri-information-line me-2"></i>
          No hay facturas {tipo} con valores extremos
        </Alert>
      );
    }

    return (
      <div className="table-responsive">
        <Table className="table-sm table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>Nº Factura</th>
              <th>Fecha</th>
              <th className="text-end">Monto USD</th>
              <th className="text-center">Tipo</th>
              <th>Observación</th>
            </tr>
          </thead>
          <tbody>
            {facturas.map((factura, index) => (
              <tr key={index}>
                <td>
                  {factura.solicitud_id ? (
                    <Link to={`/solicitud/ver/${factura.solicitud_id}`} target="_blank" rel="noopener noreferrer" className="fw-medium text-primary text-decoration-underline">
                      {factura.numero_factura} <i className="ri-external-link-line ms-1 fs-11"></i>
                    </Link>
                  ) : (
                    <span className="fw-medium">{factura.numero_factura}</span>
                  )}
                </td>
                <td>
                  <small className="text-muted">{formatearFecha(factura.fecha)}</small>
                </td>
                <td className="text-end">
                  <span className={`fw-bold ${factura.es_alto ? 'text-danger' : 'text-warning'}`}>
                    $ {formatearNumero(factura.monto_usd)}
                  </span>
                </td>
                <td className="text-center">
                  {factura.es_alto ? (
                    <Badge color="danger" className="fs-11">
                      <i className="ri-arrow-up-line"></i> Alto
                    </Badge>
                  ) : (
                    <Badge color="warning" className="fs-11">
                      <i className="ri-arrow-down-line"></i> Bajo
                    </Badge>
                  )}
                </td>
                <td>
                  <small className="text-muted">{factura.observacion || "-"}</small>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  if (!proveedor) return null;

  const outliers = proveedor.outliers_facturas || {};
  const totalOutliers = outliers.total || 0;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
      <ModalHeader toggle={toggle}>
        <div>
          <h5 className="mb-1">
            <i className="ri-alert-line text-danger me-2"></i>
            Facturas con Valores Extremos
          </h5>
          <p className="text-muted mb-0 fs-14">
            {proveedor.nombre} ({proveedor.codigo})
          </p>
        </div>
      </ModalHeader>
      
      <ModalBody>
        {/* Resumen */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card border-0 bg-light">
              <div className="card-body text-center">
                <h3 className="mb-1 text-danger">{totalOutliers}</h3>
                <p className="text-muted mb-0 fs-13">Total Outliers</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 bg-light">
              <div className="card-body text-center">
                <h3 className="mb-1 text-primary">{proveedor.total_facturas}</h3>
                <p className="text-muted mb-0 fs-13">Total Facturas</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 bg-light">
              <div className="card-body text-center">
                <h3 className="mb-1 text-warning">{outliers.porcentaje}%</h3>
                <p className="text-muted mb-0 fs-13">% Anómalas</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 bg-light">
              <div className="card-body text-center">
                <h3 className="mb-1 text-success">$ {formatearNumero(proveedor.promedio_usd)}</h3>
                <p className="text-muted mb-0 fs-13">Promedio USD</p>
              </div>
            </div>
          </div>
        </div>

        {/* Rangos Normales */}
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <Alert color="danger" className="mb-0">
              <h6 className="alert-heading fs-14 mb-2">
                <i className="ri-heart-pulse-line me-1"></i> Facturas Agudas
              </h6>
              <div className="d-flex justify-content-between">
                <span className="fs-12">Outliers: <strong>{outliers.agudas?.cantidad_outliers || 0}</strong></span>
                <span className="fs-12">Porcentaje: <strong>{outliers.agudas?.porcentaje_outliers || 0}%</strong></span>
              </div>
              <div className="mt-2 pt-2 border-top border-danger border-opacity-25">
                <small className="text-muted">
                  Rango normal: ${formatearNumero(outliers.agudas?.limite_inferior || 0)} - ${formatearNumero(outliers.agudas?.limite_superior || 0)}
                </small>
              </div>
            </Alert>
          </div>
          <div className="col-md-6">
            <Alert color="warning" className="mb-0">
              <h6 className="alert-heading fs-14 mb-2">
                <i className="ri-medicine-bottle-line me-1"></i> Facturas Crónicas
              </h6>
              <div className="d-flex justify-content-between">
                <span className="fs-12">Outliers: <strong>{outliers.cronicas?.cantidad_outliers || 0}</strong></span>
                <span className="fs-12">Porcentaje: <strong>{outliers.cronicas?.porcentaje_outliers || 0}%</strong></span>
              </div>
              <div className="mt-2 pt-2 border-top border-warning border-opacity-25">
                <small className="text-muted">
                  Rango normal: ${formatearNumero(outliers.cronicas?.limite_inferior || 0)} - ${formatearNumero(outliers.cronicas?.limite_superior || 0)}
                </small>
              </div>
            </Alert>
          </div>
        </div>

        {/* Tabs con Facturas */}
        <Nav tabs className="nav-tabs-custom">
          <NavItem>
            <NavLink
              className={tabActiva === "agudas" ? "active" : ""}
              onClick={() => setTabActiva("agudas")}
              style={{ cursor: "pointer" }}
            >
              <i className="ri-heart-pulse-line me-1"></i>
              Agudas ({outliers.agudas?.cantidad_outliers || 0})
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={tabActiva === "cronicas" ? "active" : ""}
              onClick={() => setTabActiva("cronicas")}
              style={{ cursor: "pointer" }}
            >
              <i className="ri-medicine-bottle-line me-1"></i>
              Crónicas ({outliers.cronicas?.cantidad_outliers || 0})
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={tabActiva} className="pt-3">
          <TabPane tabId="agudas">
            {cargando ? (
              <WidgetLoader message="Cargando facturas agudas..." />
            ) : error ? (
              <Alert color="danger">{error}</Alert>
            ) : (
              renderTablaFacturas(facturasOutliers.agudas, "agudas")
            )}
          </TabPane>
          <TabPane tabId="cronicas">
            {cargando ? (
              <WidgetLoader message="Cargando facturas crónicas..." />
            ) : error ? (
              <Alert color="danger">{error}</Alert>
            ) : (
              renderTablaFacturas(facturasOutliers.cronicas, "crónicas")
            )}
          </TabPane>
        </TabContent>
      </ModalBody>
      
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          <i className="ri-close-line me-1"></i>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalOutliersProveedor;
